import * as R from 'ramda';
import FileSaver from 'file-saver';
import { updateReportFromReportListSuccess } from '../../common/actions';
import { put, all, call, delay, select, takeLatest } from 'redux-saga/effects';
// components
import { openLoader, closeLoader } from '../../components/loader/actions';
import {
  checkReportFunction,
  transformSearchCriteriaBeforeReportPost,
} from '../../components/edit-report/helpers';
// features
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import { getGroupConfigsByNamesRequest } from '../configurations/sagas';
import { handleGetItemListSaga as handleGetUsersListSaga } from '../user/sagas';
import { handleGetItemListSaga as handleGetBranchListSaga } from '../branch/sagas';
import { getReferenceTypesListRequestSaga, getAllAvailableRefTypesByScopeRequestSaga } from '../reference/sagas';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// report-common
import { getMockReport, getReportSagas } from '../../report-common';
// sagas
import { visitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature report-format
import * as A from './actions';
import { FILTER_PARAMS } from './settings/filter-props';
import { exportEndpointsMap } from './settings/export-endpoints-map';
import {
  makeSelectIsEditPage,
  makeSelectUsedReport,
  makeSelectPagination,
  makeSelectTitleSortValues,
  makeSelectTableTitleFilters,
} from './selectors';
//////////////////////////////////////////////////

export function* handleGetItemListSaga({ payload } = {}) {
  try {
    if (R.path(['openLoader'], payload)) {
      yield put(openLoader({ showDimmer: true }));
    }

    yield put(A.setListLoading(true));

    const pagination = yield select(makeSelectPagination());
    const reportParams = yield select(makeSelectUsedReport());
    const titleOrderFields = yield select(makeSelectTitleSortValues());
    const titleFilterParams = yield select(makeSelectTableTitleFilters());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const requiredFields = [
      { name: GC.FIELD_OWNER, freezed: false, sequence: 100, reference: false },
      { name: GC.FIELD_TYPE, freezed: false, sequence: 101, reference: false },
    ];

    let fields = G.getOrElse(reportParams, 'fields', []);

    requiredFields.forEach((field) => {
      if (R.not(R.any(R.propEq(field.name, 'name'), fields))) {
        fields = R.concat(
          fields,
          R.of(Array, field),
        );
      }
    });

    const orderFields = G.ifElse(
      G.isNotEmpty(titleOrderFields),
      R.values(titleOrderFields),
      G.getOrElse(reportParams, 'orderFields', []),
    );

    const searchCriteria = G.getSearchCriteriaWithTitleFilters(reportParams, titleFilterParams);

    const reqBody = {
      fields,
      orderFields,
      limit: pagination.limit,
      offset: pagination.offset,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      searchCriteria: transformSearchCriteriaBeforeReportPost(searchCriteria),
    };

    const options = {
      data: reqBody,
    };

    const res = yield call(sendRequest, 'post', endpointsMap.listReports, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getItemListSuccess({ data }));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetItemListSaga fail');
    }

    yield put(A.setListLoading(false));

    if (R.path(['openLoader'], payload)) {
      yield put(closeLoader());
    }
  } catch (error) {
    yield put(A.setListLoading(false));
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleGetItemListSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  }
}

const pageSagasMap = {
  [GC.USER_REPORT]: handleGetUsersListSaga,
  [GC.REPORT_REPORT]: handleGetItemListSaga,
  [GC.BRANCH_REPORT]: handleGetBranchListSaga,
  [GC.REFERENCE_REPORT]: getReferenceTypesListRequestSaga,
};

const scopesByReportTypeMap = {
  [GC.CLO_REPORT]: [GC.REF_SCOPE_NAME_CLO],
  [GC.TEL_REPORT]: [GC.REF_SCOPE_NAME_TEL],
  [GC.USER_REPORT]: [GC.REF_SCOPE_NAME_USER],
  [GC.ITEM_REPORT]: [GC.REF_SCOPE_NAME_ITEM],
  [GC.BRANCH_REPORT]: [GC.REF_SCOPE_NAME_BRANCH],
  [GC.CLO_ITEM_REPORT]: [GC.REF_SCOPE_NAME_ITEM],
  [GC.CLO_BY_TEL_REPORT]: [GC.REF_SCOPE_NAME_CLO],
  [GC.CARRIER_REPORT]: [GC.REF_SCOPE_NAME_CARRIER],
  [GC.FLEET_TRUCK_REPORT]: [GC.REF_SCOPE_NAME_FLEET_TRUCK],
  [GC.FLEET_DRIVER_REPORT]: [GC.REF_SCOPE_NAME_FLEET_DRIVER],
  [GC.FLEET_VENDOR_REPORT]: [GC.REF_SCOPE_NAME_FLEET_VENDOR],
  [GC.LOCATION_TEMPLATE_REPORT]: [GC.REF_SCOPE_NAME_LOCATION],
  [GC.FLEET_TRAILER_REPORT]: [GC.REF_SCOPE_NAME_FLEET_TRAILER],
  [GC.CARRIER_INVOICE_REPORT]: [GC.REF_SCOPE_NAME_CARRIER_INVOICE],
  [GC.ROUTE_REPORT]: [GC.REF_SCOPE_NAME_TEL, GC.REF_SCOPE_NAME_CLO],
  [GC.ROUTE_BY_LOAD_REPORT]: [GC.REF_SCOPE_NAME_TEL, GC.REF_SCOPE_NAME_CLO],
  [GC.CLO_EVENT_REPORT]: [GC.REF_SCOPE_NAME_LOAD_EVENT, GC.REF_SCOPE_NAME_CLO],
  [GC.VENDOR_INVOICE_REPORT]: [GC.REF_SCOPE_NAME_FLEET_INVOICE, GC.REF_SCOPE_NAME_TEL],
  [GC.CUSTOMER_INVOICE_REPORT]: [GC.REF_SCOPE_NAME_CUSTOMER_INVOICE, GC.REF_SCOPE_NAME_CLO],
  [GC.DRIVER_PAYROLL_INVOICE_REPORT]: [GC.REF_SCOPE_NAME_FLEET_INVOICE, GC.REF_SCOPE_NAME_TEL],
};

const createUpdateReportSuccessCallback = (data) => G.getReportSortedBySeqFreez(data);

const {
  handleAvailableReportsRequest,
  handleChangeDefaultReportSaga: handleChangeDefaultReportSagaCommon,
} = getReportSagas(GC.REPORT_REPORT, A, handleGetItemListSaga, { createUpdateReportSuccessCallback });

export function* handleAvailableReportRequestSaga({ payload, notSetUsedReport } = {}) {
  yield call(handleAvailableReportsRequest, { notSetUsedReport });

  const reportType = R.path(['reportType'], payload);
  const pageSaga = R.prop(reportType, pageSagasMap);

  if (G.isFunction(pageSaga)) {
    yield call(pageSaga, { payload });
  }
}

export function* handleReportRequestSaga({ guid, modal }) {
  try {
    let res;
    const isEditPage = yield select(makeSelectIsEditPage());

    if (guid.includes('Default')) {
      res = G.getMockResponse(200, getMockReport(guid));
    } else {
      res = yield call(sendRequest, 'get', endpointsMap.getCurrentReportEndpoint(guid));
    }

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const scopes = R.path([data.type], scopesByReportTypeMap);

      if (G.isNotNil(scopes)) {
        yield all(scopes.map((scope) => call(getAllAvailableRefTypesByScopeRequestSaga, { payload: scope })));
      }

      const usedReport = checkReportFunction(R.of(Array, data));

      if (R.not(isEditPage)) {
        yield put(A.receivedUsedReportSuccess(...usedReport));
      }

      if (G.isFunction(modal)) yield put(A.openEditReportModal({ guid, modal, usedReport }));
    } else {
      yield call(G.handleFailResponse, res, 'handleReportRequestSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleReportRequestSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  }
}

export function* handleUpdateReportOwnerRequestSaga({ payload }) {
  try {
    yield put(openLoader());
    const res = yield call(sendRequest, 'put', endpointsMap.changeOwnerReport, { data: payload });
    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetListAndPagination());
      yield call(handleGetItemListSaga, { payload: { openLoader: true } });
    } else {
      yield call(G.handleFailResponse, res, 'handleUpdateReportOwnerRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'handleUpdateReportOwnerRequestSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  }
}

export function* handleUpdateReportRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const isEditPage = yield select(makeSelectIsEditPage());

    const searchCriteria = transformSearchCriteriaBeforeReportPost(R.path(['searchCriteria'], payload));
    const newPayload = R.set(R.lensProp('searchCriteria'), searchCriteria, payload);

    const endpoint = G.ifElse(
      R.propEq(GC.REPORT_VIEW_TYPE_PIVOT, 'viewType', payload),
      endpointsMap.reportPivot,
      endpointsMap.report,
    );

    const res = yield call(sendRequest, 'put', endpoint, { data: newPayload });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(updateReportFromReportListSuccess(data));

      yield put(A.resetListAndPagination());

      const userReport = yield select(makeSelectUsedReport());

      if (R.and(isEditPage, R.not(R.eqProps(GC.FIELD_GUID, userReport, data)))) {
        yield delay(100);

        yield put(closeLoader());
        yield put(A.getItemListRequest());
      } else {
        yield put(A.setUsedReport(G.getReportSortedBySeqFreez(
          R.set(R.lensProp('searchCriteria'),
          R.path(['searchCriteria'], payload),
          data),
        )));

        yield call(
          handleAvailableReportRequestSaga,
          {
            payload: {
              reportType: GC.REPORT_REPORT,
              pathname: GC.ROUTE_PATH_REPORTS_LIST,
            },
            notSetUsedReport: true,
          },
        );
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleUpdateReportRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleUpdateReportRequestSaga exception');
  }
}

export function* handleCreateReportRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const { type } = payload;

    const isEditPage = yield select(makeSelectIsEditPage());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const data = R.assoc(GC.FIELD_BRANCH_GUID, currentBranchGuid, payload);

    const endpoint = G.ifElse(
      R.propEq(GC.REPORT_VIEW_TYPE_PIVOT, 'viewType', payload),
      endpointsMap.reportPivot,
      endpointsMap.report,
    );

    const res = yield call(sendRequest, 'post', endpoint, { data });

    const { status } = res;

    yield put(A.resetListAndPagination());

    if (G.isResponseSuccess(status)) {
      if (R.or(isEditPage, G.notEquals(type, GC.REPORT_REPORT))) {
        yield delay(100);

        yield put(closeLoader());
        yield put(A.getItemListRequest());
      } else {
        yield put(A.setUsedReport(G.getReportSortedBySeqFreez(res.data)));

        yield call(
          handleAvailableReportRequestSaga,
          {
            payload: {
              reportType: GC.REPORT_REPORT,
              pathname: GC.ROUTE_PATH_REPORTS_LIST,
            },
            notSetUsedReport: true,
          },
        );
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateReportRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleCreateReportRequestSaga exception');
  }
}

export function* handleChangeDefaultReportSaga({ payload }) {
  const isEditPage = yield select(makeSelectIsEditPage());

  if (R.not(isEditPage)) {
    yield call(handleChangeDefaultReportSagaCommon, { payload });

    return;
  }

  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'put', endpointsMap.changeDefaultReport, { data: payload });
    const { status } = res;

    if (R.not(G.isResponseSuccess(status))) {
      yield call(G.handleFailResponse, res, 'handleChangeDefaultReportSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleChangeDefaultReportSaga exception');
  }
}

export function* handleExportReportDataSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const reqBody = {
      fields: payload.fields,
      orderFields: payload.orderFields,
      searchCriteria: payload.searchCriteria,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const options = {
      data: reqBody,
      resType: 'arraybuffer',
      params: { format: payload.fileType },
    };

    const listOrder = G.ifElse(
      payload.listType,
      `${payload.type}-${payload.listType}`,
      payload.type,
    );

    const res = yield call(sendRequest, 'post', exportEndpointsMap[listOrder], options);

    const { data, status, headers } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'messages:success:200-201');

      const file = new window.Blob(
        R.of(Array, data),
        { type: headers['content-type'] },
      );

      FileSaver.saveAs(file, `${payload.name}.xlsx`);
    } else {
      yield call(G.handleFailResponse, res, 'handleDownloadTruckSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleExportReportDataSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  }
}

export function* handleSetFilterParams() {
  yield put(A.setFilterProps(FILTER_PARAMS));
}

export function* handleVisitReportsListPageSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield call(visitPageSaga, payload, GC.CHECK_VISIT_REPORTS_LIST_PAGE);

    yield put(openLoader({ showDimmer: true }));
    yield put(A.resetListAndPagination());
    yield put(A.setEditPage(false));
    yield put(A.setReports([]));
    yield put(A.cleanQuickFilter());
    yield put(A.setIgnorePromptStatus(false));

    yield call(handleAvailableReportsRequest, { payload, notSetUsedReport: false });
    yield call(handleGetItemListSaga, { payload: { openLoader: true } });

    yield call(getGroupConfigsByNamesRequest, { payload: {
      names: GC.TRUCK_DOCUMENT_TYPE,
      groupName: GC.TRUCK_CONFIG_GROUP,
    } });
    yield call(getGroupConfigsByNamesRequest, { payload: {
      names: GC.TRAILER_DOCUMENT_TYPE,
      groupName: GC.TRAILER_CONFIG_GROUP,
    } });
    yield call(getGroupConfigsByNamesRequest, { payload: {
      names: GC.DRIVER_CONFIG_GROUP,
      groupName: `${GC.DRIVER_DOCUMENT_TYPE},${GC.DRIVER_CERTIFICATION_TYPE}`,
    } });

    yield call(handleSetFilterParams);

    yield put(closeLoader());
    break;
  }
}

export function* handleDeleteReportRequestSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));
    const url = endpointsMap.getCurrentReportEndpoint(payload.guid);
    const res = yield call(sendRequest, 'delete', url);
    const { status } = res;

    if (G.isResponseSuccess(status)) {
      const usedReport = yield select(makeSelectUsedReport());

      if (R.equals(usedReport.guid, payload.guid)) {
        const visitPayload = {
          reportType: GC.REPORT_REPORT,
          pathname: G.getCurrentPathname(),
        };

        yield call(handleVisitReportsListPageSaga, { payload: visitPayload });
      }

      yield put(A.receivedDeleteReportSuccess(payload.guid));
    } else {
      yield call(G.handleFailResponse, res, 'handleDeleteReportRequestSaga fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'handleDeleteReportRequestSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  }
}

export function* handleGetReportEditDataSaga({ payload }) {
  while (true) { // eslint-disable-line
    const { guid, modal } = payload;

    yield put(openLoader({ showDimmer: true }));
    yield put(A.setEditPage(true));
    yield call(handleReportRequestSaga, { guid, modal });
    yield put(closeLoader());
    break;
  }
}

function* reportFormatWatcherSaga() {
  yield takeLatest(A.getReportDataRequest, handleGetReportEditDataSaga);
  yield takeLatest(A.createReportRequest, handleCreateReportRequestSaga);
  yield takeLatest(A.updateReportRequest, handleUpdateReportRequestSaga);
  yield takeLatest(A.deleteReportRequest, handleDeleteReportRequestSaga);
  yield takeLatest(A.exportReportDataRequest, handleExportReportDataSaga);
  yield takeLatest(A.getItemListRequest, handleGetItemListSaga);
  yield takeLatest(GC.VISIT_REPORTS_LIST_PAGE, handleVisitReportsListPageSaga);
  yield takeLatest(A.changeDefaultReportRequest, handleChangeDefaultReportSaga);
  yield takeLatest(A.getAvailableReportsRequest, handleAvailableReportRequestSaga);
  yield takeLatest(A.updateReportOwnerRequest, handleUpdateReportOwnerRequestSaga);
}

export default reportFormatWatcherSaga;
